import { bunja } from 'bunja';
import { atom } from 'jotai';

import { i18nBunja } from '@flipster/i18n';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';

import { spotFormBunja } from './form';

// Order Form Validation Error Messages

// 1. Trigger Price Validation
// - triggerPriceCannotBeZero: "The trigger price is set to 0. \nPlease adjust the price"

// 2. Amount Validation
// - amountCannotBeZero: "Please enter value to create an order"
// - amountExceedsAvailableFunds: "Amount exceeds available asset"
// - amountBelowMinimum: "Minimum order amount is {minAmount} USDT"
// - amountAboveMaximum: "You can buy/sell up to {maxAmount} USDT"

// 3. Quantity Validation
// - quantityCannotBeZero: "Please enter quantity to create an order"
// - quantityBelowMinimum: "Minimum order amount is {minQuantity} {positionUnit}"
// - quantityAboveMaximum: "You can buy/sell up to {maxQuantity} {positionUnit}"

type ErrorMessageType = 'toast' | 'field' | 'custom';

interface ErrorMessage<T extends ErrorMessageType> {
  type: T;
  message: string;
}

export interface ValidationResult {
  triggerPrice?: ErrorMessage<'toast'>;
  amount?: ErrorMessage<'custom'>;
  quantity?: ErrorMessage<'custom'>;
}

export const validationBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { getT } = bunja.use(i18nBunja);
  const form = bunja.use(spotFormBunja);

  const transTrade = getT('Trade.r250401');
  const transRecurringBuy = getT('Recurring_buy.r250401');

  const amountErrorMessageAtom = atom<ErrorMessage<'custom'> | undefined>((get) => {
    const side = get(form.sideAtom);
    const orderType = get(form.orderStateAtom).type;
    const availableFunds = get(form.availableUsdtAtom);
    const maxOrderAmount = get(form.maxOrderAmountAtom);
    const minOrderAmount = get(form.minOrderAmountAtom);
    const amount = get(form.amountAtom);
    const isDirty = get(form.dirtyAtom).amount;

    // orderType이 amount가 아니면 해당 validate를 skip 하도록 함
    if (orderType !== 'amount' || !isDirty) return;

    if (amount.isZero())
      return {
        type: 'custom',
        message: transTrade('Missing_value_error'),
      };

    if (amount.isGreaterThan(availableFunds))
      return {
        type: 'custom',
        message: transRecurringBuy('Amount_exceeds_available_asset'),
      };

    if (amount.isLessThan(minOrderAmount))
      return {
        type: 'custom',
        message: transTrade('Min_order_amount_error', {
          amount: numberFormatter(minOrderAmount, { precision: UsdtPrecision, toFixed: true }),
          symbol: 'USDT',
        }),
      };

    if (amount.isGreaterThan(maxOrderAmount))
      return {
        type: 'custom',
        message: transTrade(side === 'Buy' ? 'Max_buy_amount_error' : 'Max_sell_amount_error', {
          amount: numberFormatter(maxOrderAmount, { precision: UsdtPrecision, toFixed: true }),
          symbol: 'USDT',
        }),
      };
    return;
  });

  const qtyErrorMessageAtom = atom<ErrorMessage<'custom'> | undefined>((get) => {
    const side = get(form.sideAtom);
    const orderType = get(form.orderStateAtom).type;
    const quantity = get(form.quantityAtom);
    const minOrderQty = get(form.minOrderQtyAtom);
    const maxOrderQty = get(form.maxOrderQtyAtom);
    const tradeQtyPrecision = get(form.tradeQtyPrecisionAtom);
    const positionUnit = get(form.positionUnitAtom);
    const isDirty = get(form.dirtyAtom).quantity;

    // orderType이 quantity가 아니면 해당 validate를 skip 하도록 함
    if (orderType !== 'quantity' || !isDirty) return;

    if (quantity.isZero())
      return {
        type: 'custom',
        message: transTrade('Missing_quantity_error'),
      };

    if (minOrderQty.isGreaterThan(quantity))
      // qty 아래 소수점보다 무조건 커야하므로 올림
      return {
        type: 'custom',
        message: transTrade('Min_order_amount_error', {
          amount: numberFormatter(minOrderQty.toString(), { method: 'round-up', precision: tradeQtyPrecision }),
          symbol: positionUnit,
        }),
      };
    if (maxOrderQty.isLessThan(quantity))
      return {
        type: 'custom',
        message: transTrade(side === 'Buy' ? 'Max_buy_amount_error' : 'Max_sell_amount_error', {
          amount: numberFormatter(maxOrderQty.toString(), {
            precision: tradeQtyPrecision,
            toFixed: true,
          }),
          symbol: positionUnit,
        }),
      };
    return;
  });

  const triggerPriceErrorMessageAtom = atom<ErrorMessage<'toast'> | undefined>((get) => {
    const triggerPrice = get(form.triggerPriceAtom);
    const priceType = get(form.priceTypeAtom);
    const isDirty = get(form.dirtyAtom).triggerPrice;
    if (priceType !== 'trigger' || !isDirty) return;

    if (Num(triggerPrice || 0).isZero())
      return {
        type: 'toast',
        message: transTrade('Trigger_price_zero_error_toast'),
      };

    return;
  });

  const validationErrorsAtom = atom((get) => {
    const isDirty = Object.values(get(form.dirtyAtom)).some(Boolean);
    if (!isDirty) return {};
    const result: ValidationResult = {};

    const triggerPriceErrorMessage = get(triggerPriceErrorMessageAtom);
    const amountErrorMessage = get(amountErrorMessageAtom);
    const qtyErrorMessage = get(qtyErrorMessageAtom);

    if (triggerPriceErrorMessage) result.triggerPrice = triggerPriceErrorMessage;
    if (amountErrorMessage) result.amount = amountErrorMessage;
    if (qtyErrorMessage) result.quantity = qtyErrorMessage;

    return result;
  });

  function checkValidationAll() {
    store.set(form.dirtyAtom, {
      orderPercent: true,
      quantity: true,
      amount: true,
      triggerPrice: true,
    });
  }

  return { validationErrorsAtom, checkValidationAll };
});
validationBunja.debugLabel = 'validationBunja';
